import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ShieldCheck, Sparkles, IndianRupee, AlertTriangle } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";

/**
 * Public rate card. Anyone (logged in or not) can see per-newspaper × per-category
 * rates with the 5% online discount applied and GST called out separately.
 * Built to make prices scannable in 5 seconds so visitors decide fast.
 */
export default function Pricing() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/pricing")
      .then(({ data }) => setData(data))
      .catch((e) => {
        console.error("[Pricing] load failed:", e);
        setErr("Unable to load rates right now. Please refresh in a moment.");
      });
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <main className="flex-1">
        {/* Hero */}
        <section className="bg-black text-white border-b border-black/10">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-14 sm:py-16">
            <div className="text-[10px] tracking-overline uppercase text-white/60 font-bold">Rate Card</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-2 font-bold tracking-tight">
              Transparent pricing.
              <span className="block text-white/60 font-normal italic">All rates for Chh. Sambhajinagar.</span>
            </h1>
            <p className="mt-5 text-sm sm:text-base text-white/70 max-w-2xl">
              Prices below already include the <strong className="text-white">5% online booking discount</strong>. GST @ 5% is charged separately on the discounted base — no surprises at checkout.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link to="/book" className="inline-flex items-center gap-2 bg-white text-black font-semibold px-5 py-3 rounded-sm hover:bg-gray-200 transition" data-testid="pricing-cta-book">
                Start booking <ArrowRight size={16}/>
              </Link>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-white/60">
                <ShieldCheck size={16}/> No account needed to view rates
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 sm:py-14">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 space-y-14">
            {err && (
              <div className="px-4 py-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm" data-testid="pricing-error">{err}</div>
            )}
            {!data && !err && (
              <div className="text-gray-500 text-sm">Loading rates…</div>
            )}

            {data && data.categories.map((cat) => (
              <div key={cat.key} data-testid={`pricing-cat-${cat.key}`}>
                <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4 border-b border-black/15 pb-3">
                  <div>
                    <div className="text-[10px] tracking-overline uppercase text-gray-500 font-semibold">Category</div>
                    <h2 className="font-display text-2xl sm:text-3xl font-bold text-black mt-0.5">
                      {cat.label} <span className="text-gray-400 font-normal text-lg sm:text-xl">· {cat.size}</span>
                    </h2>
                  </div>
                  <div className="text-[10px] sm:text-xs text-gray-500 italic max-w-md text-right">
                    Discount is <strong>5%</strong> off the flat rate. GST is <strong>5%</strong> on the discounted base.
                  </div>
                </div>

                {cat.disclaimer && (
                  <div className="mb-4 px-4 py-3 bg-yellow-50 border-l-4 border-yellow-500 text-yellow-900 text-xs sm:text-sm flex items-start gap-2" data-testid={`pricing-disclaimer-${cat.key}`}>
                    <AlertTriangle size={14} className="mt-0.5 shrink-0"/>
                    <span>{cat.disclaimer}</span>
                  </div>
                )}

                {cat.rows.length === 0 ? (
                  <div className="p-6 text-center text-gray-500 text-sm border border-dashed border-black/20 rounded-sm">
                    No newspapers currently offer this category. Please contact us.
                  </div>
                ) : (
                  <div className="overflow-x-auto border border-black/10 rounded-sm">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[10px] tracking-overline uppercase text-gray-500 font-bold bg-gray-50">
                          <th className="px-4 py-3">Newspaper</th>
                          <th className="px-4 py-3">Language</th>
                          <th className="px-4 py-3 text-right">Flat rate</th>
                          <th className="px-4 py-3 text-right text-emerald-700">− Discount</th>
                          <th className="px-4 py-3 text-right"><span className="font-bold">Price</span><br/><span className="normal-case text-[9px] text-gray-400">before GST</span></th>
                          <th className="px-4 py-3 text-right text-gray-500">+ GST 5%</th>
                          <th className="px-4 py-3 text-right"><span className="font-bold text-black">Total</span></th>
                          <th className="px-4 py-3"></th>
                        </tr>
                      </thead>
                      <tbody className="font-mono">
                        {cat.rows.map((r) => (
                          <tr key={r.paper} className="border-t border-black/10 hover:bg-gray-50 transition" data-testid={`pricing-row-${cat.key}-${r.paper.toLowerCase().replace(/\s+/g,'-')}`}>
                            <td className="px-4 py-3 font-sans font-semibold text-black">{r.paper}</td>
                            <td className="px-4 py-3 font-sans text-gray-500 text-xs">{r.language}</td>
                            <td className="px-4 py-3 text-right text-gray-700">₹{r.flat_rate.toFixed(2)}</td>
                            <td className="px-4 py-3 text-right text-emerald-700">−₹{r.discount_amount.toFixed(2)}</td>
                            <td className="px-4 py-3 text-right font-bold text-black">₹{r.discounted_base.toFixed(2)}</td>
                            <td className="px-4 py-3 text-right text-gray-500">+₹{r.gst_amount.toFixed(2)}</td>
                            <td className="px-4 py-3 text-right font-bold text-black">₹{r.total_payable.toFixed(2)}</td>
                            <td className="px-4 py-3 text-right">
                              <Link to="/book"
                                className="inline-flex items-center gap-1 text-black font-semibold text-xs hover:underline"
                                data-testid={`pricing-book-${cat.key}-${r.paper.toLowerCase().replace(/\s+/g,'-')}`}>
                                Book <ArrowRight size={12}/>
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}

            {data && (
              <div className="text-xs text-gray-500 border-t border-black/10 pt-6 flex items-start gap-2">
                <Sparkles size={14} className="mt-0.5 shrink-0"/>
                <span>{data.note}</span>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
